import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { Heart, ChatCircleDots, MapPin, ArrowLeft, Trash, PaperPlaneTilt, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function MemoryLaneDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [liked, setLiked] = useState(false);
  const [comment, setComment] = useState("");
  const [lightbox, setLightbox] = useState(null);
  useSeo(m?.title || "Memory Lane", "A shared school & college memory on AlumniPulse Memory Lane.");

  const load = () => api.get(`/public/memories/${id}`).then((r) => { setM(r.data); setLiked(user && (r.data.likes || []).includes(user.id)); }).catch(() => setM(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (m === false) return <div className="mx-auto max-w-3xl px-5 py-24 text-center text-muted-foreground">Memory not found. <Link to="/memory-lane" className="text-secondary hover:underline">Back to Memory Lane</Link></div>;
  if (!m) return <div className="mx-auto max-w-3xl px-5 py-24 text-center text-muted-foreground">Loading…</div>;

  const meta = [m.institution, m.campus, m.city, m.state, m.country].filter(Boolean).join(" · ");
  const canDelete = user && (user.id === m.created_by || user.role === "admin");

  const like = async () => {
    if (!user) return toast.info("Sign in to react");
    try { const { data } = await api.post(`/memories/${m.id}/like`); setLiked(data.liked); setM((s) => ({ ...s, likes: data.liked ? [...(s.likes || []), user.id] : (s.likes || []).filter((x) => x !== user.id) })); } catch { toast.error("Could not react"); }
  };
  const addComment = async () => {
    if (!user) return toast.info("Sign in to comment");
    if (!comment.trim()) return;
    try { const { data } = await api.post(`/memories/${m.id}/comments`, { text: comment }); setM((s) => ({ ...s, comments: [...(s.comments || []), data] })); setComment(""); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async () => { if (!window.confirm("Delete this memory?")) return; try { await api.delete(`/memories/${m.id}`); toast.success("Deleted"); nav("/memory-lane"); } catch { toast.error("Could not delete"); } };

  return (
    <>
    <section className="border-b border-border/60 bg-[#efe7d6]">
      <Link to="/memory-lane"><img src="/memory-lane-banner.webp" alt="Alumni Pulse — School & College Memory Lane" className="mx-auto block w-full max-w-[1600px]" data-testid="memory-lane-banner" /></Link>
    </section>
    <div className="mx-auto max-w-3xl px-5 py-12" data-testid="memory-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/memory-lane" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Memory Lane</Link>
        {canDelete && <Button variant="ghost" size="sm" onClick={del} className="gap-1.5 text-destructive hover:text-destructive" data-testid="memory-delete"><Trash size={15} /> Delete</Button>}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-accent px-2.5 py-0.5 font-medium text-primary">{m.category}</span>
        {m.decade && <span className="rounded-full border border-border/60 px-2.5 py-0.5 text-muted-foreground">{m.decade}</span>}
        {m.class_batch && <span className="rounded-full border border-border/60 px-2.5 py-0.5 text-muted-foreground">{m.class_batch}</span>}
      </div>
      <h1 className="mt-3 font-heading text-3xl font-light leading-tight tracking-tight sm:text-4xl">{m.title}</h1>
      <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {m.created_by_name} · {timeAgo(m.created_at)}</span>
        {meta && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {meta}</span>}
      </p>

      {(m.photo_file_ids || []).length > 0 && (
        <div className={`mt-6 grid gap-3 ${m.photo_file_ids.length === 1 ? "" : "sm:grid-cols-2"}`}>
          {m.photo_file_ids.map((fid) => (
            <button key={fid} onClick={() => setLightbox(fid)} className="overflow-hidden rounded-2xl border border-border/60">
              <img src={fileUrl(fid)} alt={m.title} className="max-h-[520px] w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {m.memory && <p className="mt-6 whitespace-pre-line text-lg leading-relaxed text-foreground/90">{m.memory}</p>}
      {m.people_shown && <p className="mt-4 text-sm text-muted-foreground"><strong>In the photo:</strong> {m.people_shown}</p>}

      <div className="mt-6 flex items-center gap-4 border-y border-border/60 py-4">
        <button onClick={like} className="inline-flex items-center gap-1.5 text-sm hover:text-secondary" data-testid="memory-detail-like">
          <Heart size={18} weight={liked ? "fill" : "regular"} className={liked ? "text-secondary" : ""} /> {(m.likes || []).length} {(m.likes || []).length === 1 ? "tribute" : "tributes"}
        </button>
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"><ChatCircleDots size={18} /> {(m.comments || []).length} comments</span>
      </div>

      {/* Comments */}
      <div className="mt-6 space-y-4">
        {user ? (
          <div className="flex gap-3">
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a memory or a comment…" data-testid="memory-comment-input" />
            <Button onClick={addComment} className="rounded-full self-end gap-1.5" data-testid="memory-comment-submit"><PaperPlaneTilt size={15} /> Post</Button>
          </div>
        ) : (
          <p className="rounded-xl border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground"><Link to="/register" className="font-medium text-secondary hover:underline">Sign in</Link> to add your own memory or comment.</p>
        )}
        {(m.comments || []).map((c) => (
          <div key={c.id} className="flex gap-3 rounded-xl border border-border/60 bg-card p-4">
            <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full bg-accent text-sm font-medium text-primary">
              {c.author_avatar ? <img src={fileUrl(c.author_avatar)} alt="" className="h-full w-full object-cover" /> : (c.author_name || "?")[0]}
            </div>
            <div>
              <p className="text-sm"><span className="font-medium">{c.author_name}</span> <span className="text-xs text-muted-foreground">· {timeAgo(c.created_at)}</span></p>
              <p className="mt-0.5 text-sm text-foreground/90">{c.text}</p>
            </div>
          </div>
        ))}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 z-50 grid place-items-center bg-black/85 p-6" data-testid="memory-lightbox">
          <img src={fileUrl(lightbox)} alt="" className="max-h-[90vh] max-w-full rounded-lg object-contain" />
        </div>
      )}
    </div>
    </>
  );
}
